<script setup lang="ts">
/**
 * Props for the component
 * @typedef {Object} Props
 * @property {string} text - The text text to display, split into words and letters.
 * @property {number} [radius=50] - The radius within which the mouse affects letter proximity.
 * @property {'linear' | 'exponential' | 'gaussian' | 'easeIn' | 'easeOut' | 'easeInOut'} [easing='linear'] - The type of easing effect to apply.
 * @property {{ from: number, to: number }} [exposure] - The growing range for the exposure font effect, from and to values.
 */
type Props = {
  text: string
  radius?: number
  easing?: 'linear' | 'exponential' | 'gaussian' | 'easeIn' | 'easeOut' | 'easeInOut'
  exposure?: { from: number; to: number }
}

const props = withDefaults(defineProps<Props>(), {
  radius: 50,
  easing: 'linear',
  exposure: () => ({ from: -20, to: -80 }),
})

const containerRef = ref<HTMLElement | null>(null)
const letterRefs = ref<HTMLElement[][]>([])
const { x: mouseX, y: mouseY } = useMouse()
const letterProximities = ref<number[]>([])
const words = props.text
  .split(/\s+/)
  .map((word) => (word.includes('-') ? word.split(/(?<=-)/) : [word]))
  .flat()
// Total letters excluding spaces
const letterCount = props.text.replace(/\s/g, '').length

// Create a computed array of offsets for each word (the cumulative number of letters up to that word)
const offsets = computed(() => {
  let count = 0
  return words.map((word) => {
    const current = count
    count += word.length
    return current
  })
})

const hasAnimated = ref(false)
const isAnimating = ref(false) // Track if the animation is currently playing

const previousProximities = new Array(props.text.replace(/\s/g, '').length).fill(0) // Stores previous proximity values for lerping

/**
 * Calculate the Euclidean distance between two points (x1, y1) and (x2, y2)
 * @param {number} x1 - The x-coordinate of the first point.
 * @param {number} y1 - The y-coordinate of the first point.
 * @param {number} x2 - The x-coordinate of the second point.
 * @param {number} y2 - The y-coordinate of the second point.
 * @returns {number} The distance between the two points.
 */
const calculateDistance = (x1: number, y1: number, x2: number, y2: number): number => {
  return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2)
}

/**
 * Linear interpolation function to smooth the transition between two values.
 * @param {number} a - The current value.
 * @param {number} b - The target value.
 * @param {number} t - The interpolation factor (0 to 1).
 * @returns {number} The interpolated value.
 */
const lerp = (a: number, b: number, t: number): number => {
  return a + (b - a) * t
}

/**
 * Calculate the easing effect based on distance.
 * The easing can be linear, exponential, gaussian, easeIn, easeOut, or easeInOut.
 * @param {number} distance - The distance from the mouse to the letter.
 * @returns {number} The easing value based on the selected type.
 */
const calculateEasing = (distance: number): number => {
  const normalizedDistance = Math.min(Math.max(1 - distance / props.radius, 0), 1)
  let easingValue: number

  switch (props.easing) {
    case 'exponential':
      easingValue = Math.pow(normalizedDistance, 2)
      break
    case 'gaussian':
      easingValue = Math.exp(-Math.pow(distance / (props.radius / 2), 2) / 2)
      break
    case 'easeIn':
      easingValue = Math.pow(normalizedDistance, 3) // Ease-in cubic
      break
    case 'easeOut':
      easingValue = 1 - Math.pow(1 - normalizedDistance, 3) // Ease-out cubic
      break
    case 'easeInOut':
      easingValue =
        normalizedDistance < 0.5
          ? Math.pow(normalizedDistance * 2, 3) / 2 // Ease-in-out cubic
          : 1 - Math.pow(1 - normalizedDistance * 2, 3) / 2
      break
    case 'linear':
    default:
      easingValue = normalizedDistance
  }

  return easingValue
}

onMounted(() => {
  nextTick(() => {
    letterRefs.value = Array(words.length)
      .fill([])
      .map(() => [])
    letterProximities.value = new Array(letterCount).fill(0)
  })

  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && !hasAnimated.value) {
        hasAnimated.value = true
        containerRef.value?.classList.add('animate-expo-bounce')
      }
    },
    { threshold: 0.5 },
  )

  if (containerRef.value) {
    observer.observe(containerRef.value)
  }

  // Detect when the animation starts and ends
  if (containerRef.value) {
    containerRef.value.addEventListener('animationstart', () => {
      isAnimating.value = true
    })
    containerRef.value.addEventListener('animationend', () => {
      isAnimating.value = false
    })
  }
})

watch([mouseX, mouseY], () => {
  if (!containerRef.value || isAnimating.value) return // Skip if animation is running

  const containerRect = containerRef.value.getBoundingClientRect()

  const relativeMouseX = mouseX.value - containerRect.left
  const relativeMouseY = mouseY.value - (containerRect.top + window.scrollY)

  // Use a running global index for the flattened letter array.
  let globalIndex = 0
  letterRefs.value.forEach((wordRef) => {
    wordRef.forEach((letterRef) => {
      if (!letterRef) {
        globalIndex++
        return
      }
      const rect = letterRef.getBoundingClientRect()
      const letterCenterX = rect.left + rect.width / 2 - containerRect.left
      const letterCenterY = rect.top + rect.height / 2 - containerRect.top
      const distance = calculateDistance(
        relativeMouseX,
        relativeMouseY,
        letterCenterX,
        letterCenterY,
      )
      const easingValue = calculateEasing(distance)

      // Lerp the easing value with the previous one to smooth out the transition
      letterProximities.value[globalIndex] = lerp(
        previousProximities[globalIndex],
        easingValue,
        0.05,
      )
      previousProximities[globalIndex] = letterProximities.value[globalIndex] // Update previous value
      globalIndex++
    })
  })
})
</script>

<template>
  <div ref="containerRef" class="wrapper relative h-fit cursor-default">
    <span class="inline">
      <span
        v-for="(word, wordIndex) in words"
        :key="wordIndex"
        class="inline-block break-words"
        aria-hidden="true"
      >
        <span
          v-for="(letter, letterIndex) in word.split('')"
          :key="letterIndex"
          :ref="
            (el) => {
              if (!letterRefs[wordIndex]) {
                letterRefs[wordIndex] = []
              }
              letterRefs[wordIndex][letterIndex] = el
            }
          "
          class="letter inline-block"
          :style="{
            '--delay': `${(letterIndex + words.slice(0, wordIndex).join(' ').split('').length) * 100}ms`, // Accumulate delay across words
            '--min-expo': `${exposure.from}`,
            '--max-expo': `${exposure.to}`,
            'font-variation-settings': `'EXPO' ${Math.min(
              Math.max(
                -letterProximities[offsets[wordIndex] + letterIndex] * 100,
                props.exposure.to,
              ),
              props.exposure.from,
            )}`,
          }"
        >
          {{ letter }}
        </span>

        <span v-if="wordIndex < words.length - 1 && words[wordIndex]?.slice(-1) != '-'" class="inline-block">{{}}&nbsp;</span>
      </span>
      <span class="sr-only break-words">{{ text }}</span>
    </span>
  </div>
</template>

<style>
@keyframes expo-bounce {
  from {
    font-variation-settings: 'EXPO' var(--min-expo);
  }
  50% {
    font-variation-settings: 'EXPO' var(--max-expo);
  }
  to {
    font-variation-settings: 'EXPO' var(--min-expo);
  }
}

.animate-expo-bounce .letter {
  animation: expo-bounce 1.5s ease-in-out alternate var(--delay);
}
</style>
